import { Controller } from "@hotwired/stimulus";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";

export default class extends Controller {
  static targets = ["input", "startDate", "endDate"];
  static values = {
    startDate: String,
    endDate: String
  }

  connect() {
    this.startDate = null;
    this.endDate = null;
    this.picker = null;
    
    // Solo inicializar si el elemento está visible
    if (this.element.offsetParent !== null || this.element.style.display !== 'none') {
      this.initializeDatepicker();
    } else {
      // Si está oculto, esperar a que se muestre
      this.observeVisibility();
    }
  }

  observeVisibility() {
    // Observar cambios en la visibilidad del elemento
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const isVisible = this.element.offsetParent !== null || 
                           this.element.style.display !== 'none' ||
                           window.getComputedStyle(this.element).display !== 'none';
          if (isVisible && !this.picker) {
            this.initializeDatepicker();
            observer.disconnect();
          }
        }
      });
    });

    observer.observe(this.element, {
      attributes: true,
      attributeFilter: ['style']
    });

    // También verificar periódicamente (fallback)
    const checkInterval = setInterval(() => {
      const isVisible = this.element.offsetParent !== null || 
                       this.element.style.display !== 'none' ||
                       window.getComputedStyle(this.element).display !== 'none';
      if (isVisible && !this.picker) {
        this.initializeDatepicker();
        clearInterval(checkInterval);
        observer.disconnect();
      }
    }, 100);
  }

  disconnect() {
    if (this.picker) {
      this.picker.destroy();
    }
  }

  initializeDatepicker() {
    // Verificar que flatpickr esté disponible
    if (typeof flatpickr === 'undefined') {
      console.error('flatpickr no está disponible');
      return;
    }

    // Si ya hay un picker, destruirlo primero
    if (this.picker) {
      this.picker.destroy();
      this.picker = null;
    }

    if (!this.hasInputTarget) {
      console.error('No se encontró el input target');
      return;
    }

    const options = {
      mode: "range",
      dateFormat: "Y-m-d",
      locale: {
        firstDayOfWeek: 1,
        weekdays: {
          shorthand: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
          longhand: ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"]
        },
        months: {
          shorthand: ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"],
          longhand: ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"]
        }
      },
      onChange: (selectedDates, dateStr, instance) => {
        if (selectedDates.length === 2) {
          this.startDate = selectedDates[0];
          this.endDate = selectedDates[1];
          
          // Crear o actualizar campos ocultos
          this.updateHiddenFields();
        } else if (selectedDates.length === 1) {
          this.startDate = selectedDates[0];
          this.endDate = null;
        } else {
          this.startDate = null;
          this.endDate = null;
        }
      }
    };

    try {
      this.picker = flatpickr(this.inputTarget, options);
    } catch (error) {
      console.error('Error al inicializar flatpickr:', error);
    }
  }

  updateHiddenFields() {
    if (!this.startDate || !this.endDate) return;

    const form = this.element.closest('form');
    if (!form) return;

    // Formatear fechas
    const startDateStr = this.formatDate(this.startDate);
    const endDateStr = this.formatDate(this.endDate);

    // Crear o actualizar campos ocultos
    let startInput = form.querySelector('#hidden_start_date');
    if (!startInput) {
      startInput = document.createElement('input');
      startInput.type = 'hidden';
      startInput.id = 'hidden_start_date';
      startInput.name = 'custom_start_date';
      form.appendChild(startInput);
    }
    startInput.value = startDateStr;

    let endInput = form.querySelector('#hidden_end_date');
    if (!endInput) {
      endInput = document.createElement('input');
      endInput.type = 'hidden';
      endInput.id = 'hidden_end_date';
      endInput.name = 'custom_end_date';
      form.appendChild(endInput);
    }
    endInput.value = endDateStr;
  }

  // Método para limpiar cuando se oculta el campo
  clear() {
    if (this.picker) {
      this.picker.clear();
    }
    this.startDate = null;
    this.endDate = null;
  }

  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
